import { tokens } from "@fluentui/react-components";
import {
  ArrowSort16Regular,
  CalendarLtr16Regular,
  CheckmarkCircle12Regular,
  CheckmarkCircle16Regular,
  Circle12Regular,
  Circle16Regular,
  CircleHalfFill12Regular,
  CircleHalfFill16Regular,
  DismissCircle12Regular,
  DismissCircle16Regular,
  EyeOff16Regular,
  Flag16Regular,
  Folder16Regular,
  LayoutColumnThree16Regular,
  LayoutRowThree16Regular,
  Organization16Regular,
  Person16Regular,
  QuestionCircle12Regular,
  QuestionCircle16Regular,
  Subtract16Regular,
  Tag16Regular,
} from "@fluentui/react-icons";
import type { ReactElement } from "react";
import type { ExecutionPriority, ExecutionStatus } from "@/engine/types";
import type { GroupKey } from "./task-query";

export type GlyphSize = 12 | 16;

/** 상태 표식. 표 셀은 12, 메뉴·셀렉트는 16. 색은 표식에만 싣고 글자는 본문색이다. */
export function statusGlyph(status: ExecutionStatus | undefined, size: GlyphSize = 16, tone = false): ReactElement {
  const color = !tone
    ? undefined
    : status === "done"
      ? tokens.colorPaletteGreenForeground1
      : status === "inProgress"
        ? tokens.colorBrandForeground1
        : status === "blocked"
          ? tokens.colorPaletteRedForeground1
          : tokens.colorNeutralForeground3;
  const style = color ? { color } : undefined;
  if (size === 12) {
    switch (status) {
      case "done":
        return <CheckmarkCircle12Regular style={style} />;
      case "inProgress":
        return <CircleHalfFill12Regular style={style} />;
      case "blocked":
        return <DismissCircle12Regular style={style} />;
      case "notStarted":
        return <Circle12Regular style={style} />;
      default:
        return <QuestionCircle12Regular style={style} />;
    }
  }
  switch (status) {
    case "done":
      return <CheckmarkCircle16Regular style={style} />;
    case "inProgress":
      return <CircleHalfFill16Regular style={style} />;
    case "blocked":
      return <DismissCircle16Regular style={style} />;
    case "notStarted":
      return <Circle16Regular style={style} />;
    default:
      return <QuestionCircle16Regular style={style} />;
  }
}

/** 같은 자리의 막대. 채운 개수로 단계를 나누고, 긴급·중요만 색을 쓴다. */
function PriorityBars({ active, color, size }: { active: 1 | 2 | 3 | 4; color: string; size: GlyphSize }) {
  const heights = [5, 8, 11, 14];
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden focusable="false">
      {heights.map((height, index) => (
        <rect
          key={height}
          x={1.5 + index * 3.75}
          y={15 - height}
          width="2.5"
          height={height}
          rx="0.75"
          fill={index < active ? color : tokens.colorNeutralStroke2}
        />
      ))}
    </svg>
  );
}

/** 우선순위 표식. 막대 수로 구분하고, 긴급은 빨강·중요는 주황이다. 미정은 빼기. */
export function priorityGlyph(priority: ExecutionPriority | undefined, size: GlyphSize = 16): ReactElement {
  switch (priority) {
    case "urgent":
      return <PriorityBars active={4} color={tokens.colorPaletteRedForeground1} size={size} />;
    case "important":
      return <PriorityBars active={3} color={tokens.colorPaletteDarkOrangeForeground1} size={size} />;
    case "medium":
      return <PriorityBars active={2} color={tokens.colorNeutralForeground2} size={size} />;
    case "low":
      return <PriorityBars active={1} color={tokens.colorNeutralForeground3} size={size} />;
    default:
      return <Subtract16Regular style={{ color: tokens.colorNeutralForeground3, width: size, height: size }} />;
  }
}

export type TaskFieldIcon = "status" | "priority" | "assignee" | "due" | "bucket" | "label" | "scope" | "hideDone" | "undecided" | "sort" | "group" | "column";

/** 필터·표시·칩·그룹 머리가 같이 쓰는 필드 아이콘. */
export function taskFieldIcon(field: TaskFieldIcon): ReactElement {
  switch (field) {
    case "status":
      return <Circle16Regular />;
    case "priority":
      return <Flag16Regular />;
    case "assignee":
      return <Person16Regular />;
    case "due":
      return <CalendarLtr16Regular />;
    case "bucket":
      return <Folder16Regular />;
    case "label":
      return <Tag16Regular />;
    case "scope":
      return <Organization16Regular />;
    case "hideDone":
      return <EyeOff16Regular />;
    case "undecided":
      return <QuestionCircle16Regular />;
    case "sort":
      return <ArrowSort16Regular />;
    case "group":
      return <LayoutRowThree16Regular />;
    case "column":
      return <LayoutColumnThree16Regular />;
  }
}

/** 그룹 축 하나당 아이콘 하나. 값마다 다른 그림을 만들지 않는다. */
export function groupAxisIcon(group: GroupKey): ReactElement | null {
  switch (group) {
    case "none":
      return null;
    case "status":
      return taskFieldIcon("status");
    case "priority":
      return taskFieldIcon("priority");
    case "assignee":
      return taskFieldIcon("assignee");
    case "due":
      return taskFieldIcon("due");
    case "bucket":
      return taskFieldIcon("bucket");
    case "label":
      return taskFieldIcon("label");
    case "scope":
    case "scopeRoot":
      return taskFieldIcon("scope");
  }
}
